import { useEffect, useMemo, useRef, useState } from 'react';

function PropertyImageCarousel({
  images = [],
  fallbackImage = '',
  alt = 'Property image',
  className = '',
}) {
  const imageSet = useMemo(() => {
    const deduped = [
      ...new Set((Array.isArray(images) ? images : []).filter(Boolean)),
    ];
    if (deduped.length) {return deduped;}
    return fallbackImage ? [fallbackImage] : [];
  }, [images, fallbackImage]);

  const [activeIndex, setActiveIndex] = useState(0);
  const swipeStartX = useRef(null);
  const swipeStartY = useRef(null);
  const isDragging = useRef(false);
  const isHorizontalSwipe = useRef(false);
  const containerRef = useRef(null);

  useEffect(() => {
    setActiveIndex(0);
  }, [imageSet.length]);

  if (!imageSet.length) {
    return <div className={`property-image-carousel ${className}`.trim()} />;
  }

  const hasMultiple = imageSet.length > 1;

  const goPrev = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setActiveIndex((current) =>
      current === 0 ? imageSet.length - 1 : current - 1,
    );
  };

  const goNext = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setActiveIndex((current) =>
      current === imageSet.length - 1 ? 0 : current + 1,
    );
  };

  // ── Pointer events (mouse + stylus; also works on most Android) ──────────
  const handlePointerDown = (event) => {
    // Don't track swipe if the user clicked a button (arrow or dot)
    if (event.target.closest('button')) {return;}
    if (event.button !== 0 && event.pointerType === 'mouse') {return;}
    swipeStartX.current = event.clientX;
    swipeStartY.current = event.clientY;
    isDragging.current = false;
    isHorizontalSwipe.current = false;
    // Do NOT call setPointerCapture — it steals events from child buttons
  };

  const handlePointerMove = (event) => {
    if (swipeStartX.current === null) {return;}
    const dx = Math.abs(event.clientX - swipeStartX.current);
    const dy = Math.abs(event.clientY - swipeStartY.current);
    // Same threshold as handleTouchMove below - pointer and touch events
    // both fire for the same gesture on many Android browsers and share
    // isHorizontalSwipe, so a looser threshold here could still lock it in
    // before handleTouchMove ever re-evaluates.
    if (!isHorizontalSwipe.current && (dx > 10 || dy > 10)) {
      isHorizontalSwipe.current = dx > dy * 1.5;
    }
    if (isHorizontalSwipe.current && dx > 6) {
      isDragging.current = true;
    }
  };

  const handlePointerUp = (event) => {
    if (swipeStartX.current === null || !hasMultiple) {
      swipeStartX.current = null;
      isDragging.current = false;
      return;
    }
    const delta = event.clientX - swipeStartX.current;
    swipeStartX.current = null;
    if (!isHorizontalSwipe.current || Math.abs(delta) < 40) {
      isDragging.current = false;
      return;
    }
    if (delta < 0) {
      setActiveIndex((current) =>
        current === imageSet.length - 1 ? 0 : current + 1,
      );
    } else {
      setActiveIndex((current) =>
        current === 0 ? imageSet.length - 1 : current - 1,
      );
    }
    isDragging.current = false;
  };

  // ── Touch events — reliable fallback for iOS Safari ──────────────────────
  const handleTouchStart = (event) => {
    if (!hasMultiple) {return;}
    // Don't track swipe if the user tapped a button (arrow or dot)
    if (event.target.closest('button')) {return;}
    swipeStartX.current = event.touches[0].clientX;
    swipeStartY.current = event.touches[0].clientY;
    isDragging.current = false;
    isHorizontalSwipe.current = false;
  };

  const handleTouchMove = (event) => {
    if (swipeStartX.current === null) {return;}
    const dx = Math.abs(event.touches[0].clientX - swipeStartX.current);
    const dy = Math.abs(event.touches[0].clientY - swipeStartY.current);
    // Require a clearly horizontal gesture before locking in and calling
    // preventDefault() below - a couple of pixels of horizontal wobble at
    // the start of an intended vertical scroll (very common with an
    // imprecise touch) was enough to trip `dx > dy` at the old 6px
    // threshold, which then blocked native page scroll for the rest of
    // that touch gesture (page felt "frozen" until the finger lifted).
    if (!isHorizontalSwipe.current && (dx > 10 || dy > 10)) {
      isHorizontalSwipe.current = dx > dy * 1.5;
    }
    // Prevent page scroll only when swiping horizontally
    if (isHorizontalSwipe.current) {
      event.preventDefault();
      isDragging.current = true;
    }
  };

  const handleTouchEnd = (event) => {
    if (swipeStartX.current === null || !hasMultiple) {
      swipeStartX.current = null;
      isDragging.current = false;
      return;
    }
    const delta = event.changedTouches[0].clientX - swipeStartX.current;
    swipeStartX.current = null;
    if (!isHorizontalSwipe.current || Math.abs(delta) < 40) {
      isDragging.current = false;
      return;
    }
    if (delta < 0) {
      setActiveIndex((current) =>
        current === imageSet.length - 1 ? 0 : current + 1,
      );
    } else {
      setActiveIndex((current) =>
        current === 0 ? imageSet.length - 1 : current - 1,
      );
    }
    isDragging.current = false;
  };

  const handleCardClick = (event) => {
    if (isDragging.current) {
      event.preventDefault();
      event.stopPropagation();
      isDragging.current = false;
    }
  };

  const handleKeyDown = (event) => {
    if (!hasMultiple) {return;}
    if (event.key === 'ArrowLeft') {
      event.stopPropagation();
      setActiveIndex((current) =>
        current === 0 ? imageSet.length - 1 : current - 1,
      );
    } else if (event.key === 'ArrowRight') {
      event.stopPropagation();
      setActiveIndex((current) =>
        current === imageSet.length - 1 ? 0 : current + 1,
      );
    }
  };

  return (
    <div
      ref={containerRef}
      className={`property-image-carousel ${className}`.trim()}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={() => {
        swipeStartX.current = null;
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      role="region"
      aria-label="Property images"
      tabIndex={0}
    >
      <img
        src={imageSet[activeIndex]}
        alt={alt}
        className="property-image-carousel__image"
        loading="lazy"
        draggable={false}
        onError={(event) => {
          event.currentTarget.onerror = null;
          if (fallbackImage) {
            event.currentTarget.src = fallbackImage;
          }
        }}
      />

      {hasMultiple && (
        <>
          <button
            type="button"
            className="property-image-carousel__control property-image-carousel__control--prev"
            aria-label="Previous image"
            onClick={goPrev}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button
            type="button"
            className="property-image-carousel__control property-image-carousel__control--next"
            aria-label="Next image"
            onClick={goNext}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
          <div
            className="property-image-carousel__dots"
            aria-label="Image pager"
          >
            {imageSet.map((image, index) => (
              <button
                key={`${image}-${index}`}
                type="button"
                className={`property-image-carousel__dot ${
                  index === activeIndex ? 'active' : ''
                }`.trim()}
                aria-label={`Show image ${index + 1}`}
                aria-current={index === activeIndex ? 'true' : undefined}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setActiveIndex(index);
                }}
              />
            ))}
          </div>
          <span className="property-image-carousel__counter">
            {activeIndex + 1}/{imageSet.length}
          </span>
        </>
      )}
    </div>
  );
}

export default PropertyImageCarousel;
